// apps/web/src/components/miner/EpochHistoryList.tsx
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckIcon,
  ClockIcon,
  AlertCircleIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  MinusIcon,
  CaretDownIcon,
} from './icons.js';
import { formatGNK } from './minerFormat.js';
import { useMinerRewardNodes } from '../../hooks/useMinerRewardNodes.js';
import { EpochDrillDown } from './EpochDrillDown.js';
import type { RewardEntry } from '../../lib/minerApi.js';

interface Props {
  readonly address: string;
  readonly rewards: readonly RewardEntry[];
  readonly hasNext: boolean;
  readonly onLoadMore: () => void;
  readonly initialLimit?: number; // default 10
}

export function EpochHistoryList({ address, rewards, hasNext, onLoadMore, initialLimit = 10 }: Props): ReactNode {
  const { t } = useTranslation();
  const [revealed, setRevealed] = useState<number>(initialLimit);
  const [expandedEpoch, setExpandedEpoch] = useState<number | null>(null);

  if (rewards.length === 0) {
    return (
      <div className="rounded-2xl bg-surface-2 px-4 py-6 text-center">
        <div className="mx-auto mb-2 flex h-7 w-7 items-center justify-center rounded-full bg-surface-3 text-text-tertiary">
          <AlertCircleIcon />
        </div>
        <div className="text-xs text-text-secondary">{t('miner.epochEmpty')}</div>
      </div>
    );
  }

  const visible = rewards.slice(0, revealed);
  const remaining = rewards.length - visible.length;

  function handleLoadMore() {
    if (remaining > 0) {
      setRevealed((r) => Math.min(r + 20, rewards.length));
    } else if (hasNext) {
      onLoadMore();
      setRevealed((r) => r + 20);
    }
  }

  const showLoadMore = remaining > 0 || hasNext;

  return (
    <>
      <div className="mb-2 mt-1.5 flex items-baseline justify-between px-0.5">
        <div className="text-[11px] font-semibold uppercase tracking-[1.2px] text-text-tertiary">
          {t('miner.epochHistoryTitle')}
        </div>
        <div className="font-mono text-[11px] text-text-tertiary">
          {t('miner.epochHistoryShownLast', { count: visible.length })}
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl bg-surface-2">
        {visible.map((r, i) => {
          const expanded = expandedEpoch === r.epoch_id;
          return (
            <div key={r.epoch_id}>
              <Row
                reward={r}
                expanded={expanded}
                first={i === 0}
                onClick={() => setExpandedEpoch(expanded ? null : r.epoch_id)}
              />
              <AnimatePresence initial={false}>
                {expanded && (
                  <motion.div
                    key="drill"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.22, ease: 'easeOut' }}
                    style={{ overflow: 'hidden' }}
                    className="border-t border-border-hairline bg-surface-1"
                  >
                    <DrillDownLoader address={address} epochId={r.epoch_id} />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>

      {showLoadMore && (
        <button
          type="button"
          onClick={handleLoadMore}
          className="mt-3 flex w-full items-center justify-center gap-1.5 px-2.5 py-2.5 text-sm font-medium text-link"
        >
          {t('miner.epochLoadMore', { count: remaining })}
          <CaretDownIcon width={12} height={12} strokeWidth={1.6} />
        </button>
      )}
    </>
  );
}

function Row({
  reward,
  expanded,
  first,
  onClick,
}: {
  reward: RewardEntry;
  expanded: boolean;
  first: boolean;
  onClick: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const skipped = reward.assigned_reward === 0;
  const status: 'claimed' | 'pending' | 'skipped' = skipped
    ? 'skipped'
    : reward.claimed
      ? 'claimed'
      : 'pending';

  return (
    <div
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); }
      }}
      className={`grid grid-cols-[46px_1fr_auto_auto] items-center gap-2.5 px-3.5 py-2.5 cursor-pointer transition-colors ${
        first ? '' : 'border-t border-border-hairline'
      } ${expanded ? 'bg-link-tint' : 'hover:bg-white-tint'}`}
    >
      <div className="rounded-lg bg-surface-3 py-1 text-center font-mono text-xs font-medium text-text-secondary">
        #{reward.epoch_id}
      </div>
      <div className="min-w-0 flex flex-col gap-0.5">
        {reward.duration && (
          <div className="font-mono text-[13px] font-medium text-text-secondary">{reward.duration}</div>
        )}
        {reward.duration_change_percent != null && (
          <DurationDelta percent={reward.duration_change_percent} />
        )}
      </div>
      <div className={`font-mono text-sm font-semibold tracking-[-0.01em] text-right ${
        skipped ? 'text-text-tertiary font-normal' : 'text-text-primary'
      }`}>
        {skipped ? t('miner.epochSkipped') : formatGNK(reward.assigned_reward)}
      </div>
      <StatusBadge status={status} />
    </div>
  );
}

function DurationDelta({ percent }: { percent: number }): ReactNode {
  const cls =
    percent > 0 ? 'text-pos' : percent < 0 ? 'text-neg' : 'text-text-tertiary';
  const Icon =
    percent > 0 ? ChevronUpIcon : percent < 0 ? ChevronDownIcon : MinusIcon;
  return (
    <span className={`inline-flex w-fit items-center gap-1 font-mono text-[11px] ${cls}`}>
      <Icon width={10} height={10} strokeWidth={1.6} />
      {Math.abs(percent)}%
    </span>
  );
}

function StatusBadge({ status }: { status: 'claimed' | 'pending' | 'skipped' }): ReactNode {
  const { t } = useTranslation();
  // Minimalist neutral scale: same subtle surface-3 background; icon color
  // steps from primary (claimed, most "done") → secondary (pending) → tertiary
  // (skipped, muted). The icon shape carries the semantic, not color.
  if (status === 'claimed') {
    return (
      <span title={t('minerDetail.claimed')} className="flex h-6 w-6 items-center justify-center rounded-full bg-surface-3 text-text-primary">
        <CheckIcon width={12} height={12} strokeWidth={1.6} />
      </span>
    );
  }
  if (status === 'pending') {
    return (
      <span title={t('minerDetail.pendingClaim')} className="flex h-6 w-6 items-center justify-center rounded-full bg-surface-3 text-text-secondary">
        <ClockIcon width={12} height={12} strokeWidth={1.6} />
      </span>
    );
  }
  return (
    <span title={t('minerDetail.noReward')} className="flex h-6 w-6 items-center justify-center rounded-full bg-surface-3 text-text-tertiary">
      <AlertCircleIcon width={12} height={12} strokeWidth={1.6} />
    </span>
  );
}

function DrillDownLoader({ address, epochId }: { address: string; epochId: number }): ReactNode {
  const { data, isLoading, isError, refetch } = useMinerRewardNodes(address, epochId);
  if (isLoading) return <EpochDrillDown state={{ status: 'loading' }} />;
  if (isError || !data) return <EpochDrillDown state={{ status: 'error', onRetry: () => refetch() }} />;
  return <EpochDrillDown state={{ status: 'success', data }} />;
}
